import React, { memo, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ChevronDown, Pencil, Trash2, ArrowRight } from 'lucide-react';
import moment from 'moment';
import Sparkline from './Sparkline';
import {
  findKpiForYear,
  buildKpiTrail,
  latestReading,
  reverseReading,
  describeFreshness,
  describeActivity,
  describeReadingProblem,
  readingStateLabel,
} from '../utils/meterFilters';
import { isDrawableTrend } from '../utils/meterTrends';

/**
 * Single meter in the meters list.
 *
 * The card is a disclosure: it opens on the identity a reader scans a grid for
 * — name, state, reading, trend and KPI trail — and holds back the reference
 * fields and the actions until asked. Twenty cards each carrying seven
 * labelled fields is most of a screen spent on values nobody is reading.
 *
 * A bar along the foot of the card is what discloses, which leaves the card
 * body free to open the meter on one click as it always has. A card that
 * toggled on its body would have to give navigation up, and would offer
 * nothing to say it expands at all. Every control inside stops propagation so
 * pressing one does not also navigate.
 *
 * The bar is pushed to the bottom of the card, so a row of cards of unequal
 * content still has its controls on one line.
 *
 * Expansion is held per card rather than by the list so a background refresh
 * after an edit leaves open cards open.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {string} financialYear - Financial year whose KPI mapping to display.
 * @param {Function} onEdit - Called with the meter when Edit is pressed.
 * @param {Function} onDelete - Called with the meter when Delete is pressed.
 * @param {Function} onClick - Called with the meter to open its details page.
 * @param {boolean} canEdit - Whether the current user may edit meters.
 * @param {boolean} canDelete - Whether the current user may delete meters.
 * @param {Array<number>} trend - Daily consumption for this meter, newest last.
 * @param {Array<object>} alerts - Alert instances raised against this meter,
 *   which date how long it has been reporting normally.
 * @param {number} index - Position in the grid, used to stagger the entrance.
 */
const MeterCard = ({
  meter,
  financialYear,
  onEdit,
  onDelete,
  onClick,
  canEdit = false,
  canDelete = false,
  trend,
  alerts,
  index = 0,
}) => {
  const kpi = findKpiForYear(meter, financialYear);
  const trail = buildKpiTrail(kpi);
  const reading = latestReading(meter);
  const reverse = reverseReading(meter);
  const freshness = describeFreshness(meter);
  const activity = describeActivity(meter, alerts);
  const readingProblem = describeReadingProblem(meter);

  /**
   * An inactive meter is dated from its last reading, so "Inactive Since" and
   * "Last Reported" would carry the same instant under two headings. The
   * former says more, so it stands in for both.
   */
  const sinceIsLastReported = !activity.active && Boolean(activity.since);

  const trendTone = (() => {
    if (!kpi) return 'warn';
    if (!meter.isActive) return 'bad';
    return meter.readingState === 'VALID' ? 'good' : 'warn';
  })();

  const stateClass = (() => {
    if (!kpi) return 'im-card--nokpi';
    return meter.isActive ? 'im-card--active' : 'im-card--inactive';
  })();

  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback((event) => {
    event.stopPropagation();
    setExpanded((open) => !open);
  }, []);

  const handleEdit = (event) => {
    event.stopPropagation();
    onEdit(meter);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    onDelete(meter);
  };

  const handleOpen = (event) => {
    event.stopPropagation();
    onClick(meter);
  };

  return (
    <article
      className={`im-card ${stateClass}`}
      style={{ animationDelay: `${Math.min(index, 11) * 0.03}s` }}
      role="button"
      tabIndex={0}
      title="Open meter details"
      onClick={() => onClick(meter)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(meter);
        }
      }}
    >
      <div className="im-card-head">
        <h3 className="im-card-title">{meter.meterName}</h3>
        <span
          className={`im-pill ${meter.isActive ? 'im-pill--active' : 'im-pill--inactive'}`}
          title={activity.label || undefined}
        >
          <FontAwesomeIcon
            icon={faCircle}
            className={meter.isActive ? 'im-dot im-dot--live' : 'im-dot'}
            size="xs"
          />
          {meter.isActive ? 'Active' : 'Inactive'}
          {activity.age && <span className="im-pill-age">{activity.age}</span>}
        </span>
      </div>

      <div className="im-reading">
        {reading === null ? (
          <span className="im-reading-empty">No reading received</span>
        ) : (
          <>
            <span className="im-reading-value">
              {reading.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </span>
            <span className="im-reading-unit">{meter.unit}</span>
            <span className="im-reading-caption">Forward totalizer</span>
          </>
        )}
        {readingProblem && (
          <span
            className="im-freshness im-freshness--bad"
            title={
              readingProblem.age
                ? `Last usable reading ${readingProblem.age} ago`
                : undefined
            }
          >
            {readingProblem.label}
            {readingProblem.age && (
              <span className="im-pill-age">{readingProblem.age}</span>
            )}
          </span>
        )}
        {meter.isActive && freshness && (
          <span className={`im-freshness im-freshness--${freshness.tone}`}>
            {freshness.label}
          </span>
        )}
      </div>

      {isDrawableTrend(trend) && (
        <Sparkline
          values={trend}
          tone={trendTone}
          label={`Daily consumption for ${meter.meterName}, last 14 days`}
        />
      )}

      {trail.length > 0 ? (
        <div className="im-trail">
          {trail.map((step, position) => (
            <React.Fragment key={step}>
              {position > 0 && <span className="im-trail-sep">&rsaquo;</span>}
              <span
                className={
                  position === trail.length - 1
                    ? 'im-trail-item im-trail-item--last'
                    : 'im-trail-item'
                }
              >
                {step}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="im-kpi-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>
            <strong>KPI Not Configured</strong>
            Configure KPI to view analytics
          </span>
        </div>
      )}

      {expanded && (
        <>
          <dl className="im-meta">
            <div>
              <dt>Meter ID</dt>
              <dd className="im-mono im-meta--id">{meter.meterId}</dd>
            </div>
            <div>
              <dt>Gateway ID</dt>
              <dd className="im-mono im-meta--gateway">{meter.gatewayId}</dd>
            </div>
            <div>
              <dt>Measurement Type</dt>
              <dd className="im-meta--type">{meter.measurementType}</dd>
            </div>
            <div>
              <dt>Unit</dt>
              <dd className="im-meta--unit">{meter.unit}</dd>
            </div>
            {reverse !== null && (
              <div>
                <dt>Reverse Flow</dt>
                <dd className="im-meta--reverse">
                  {reverse.toLocaleString(undefined, { maximumFractionDigits: 1 })} {meter.unit}
                </dd>
              </div>
            )}
            <div>
              <dt>Reading State</dt>
              <dd className="im-meta--state">{readingStateLabel(meter.readingState)}</dd>
            </div>
            {activity.since && (
              <div>
                <dt>{activity.active ? 'Active Since' : 'Inactive Since'}</dt>
                <dd className="im-meta--since">
                  {moment(activity.since).format('DD MMM YYYY, HH:mm')}
                  <span className="im-meta-age">{activity.age}</span>
                </dd>
              </div>
            )}
            {!sinceIsLastReported && (
              <div>
                <dt>Last Reported</dt>
                <dd className="im-meta--seen">
                  {meter.lastSeenAt
                    ? moment(meter.lastSeenAt).format('DD MMM YYYY, HH:mm')
                    : moment(meter.updatedAt).format('DD MMM YYYY, HH:mm')}
                </dd>
              </div>
            )}
          </dl>

          <div className="im-card-actions">
            {canEdit && (
              <button
                type="button"
                className="im-action im-action--icon"
                aria-label="Edit meter"
                title="Edit meter"
                onClick={handleEdit}
              >
                <Pencil aria-hidden="true" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="im-action im-action--icon im-action--danger"
                aria-label="Delete meter"
                title="Delete meter"
                onClick={handleDelete}
              >
                <Trash2 aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="im-action im-action--icon im-action--open"
              aria-label="Open meter details"
              title="Open meter details"
              onClick={handleOpen}
            >
              <ArrowRight aria-hidden="true" />
            </button>
          </div>
        </>
      )}

      <button
        type="button"
        className={`im-card-expand${expanded ? ' im-card-expand--open' : ''}`}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide meter details' : 'Show meter details'}
        onClick={toggle}
      >
        <ChevronDown aria-hidden="true" />
      </button>
    </article>
  );
};

export default memo(MeterCard);
